import {useRef, useState} from 'react';

import {equals, isNotNil} from 'ramda';

import type {AssistantEntityContext} from '@prompt-bridge/shared/ai-assistant-core';

import {aiAssistantContextStore} from '../store/aiAssistantContextStore';

type ContextOwnerToken = object;

let activeEntityContextOwnerToken: ContextOwnerToken | undefined;

export function useAssistantEntityContext(entities: AssistantEntityContext | undefined) {
  const lastPublishedRef = useRef<AssistantEntityContext | undefined>(undefined);
  const [ownerToken] = useState<ContextOwnerToken>(() => ({}));
  const publish = (nextEntities: AssistantEntityContext | undefined) => {
    const ownsPublishedContext = activeEntityContextOwnerToken === ownerToken;

    if (equals(lastPublishedRef.current, nextEntities) && ownsPublishedContext) {
      return;
    }

    lastPublishedRef.current = nextEntities;

    if (isNotNil(nextEntities)) {
      activeEntityContextOwnerToken = ownerToken;
      aiAssistantContextStore.patchContext({entities: nextEntities});
      return;
    }

    if (ownsPublishedContext) {
      activeEntityContextOwnerToken = undefined;
      aiAssistantContextStore.patchContext({entities: undefined});
    }
  };
  const [ownerRef] = useState(() => (node: HTMLDivElement | null) => {
    if (isNotNil(node)) {
      publish(entities);
      return;
    }

    if (activeEntityContextOwnerToken === ownerToken) {
      activeEntityContextOwnerToken = undefined;
      lastPublishedRef.current = undefined;
      aiAssistantContextStore.patchContext({entities: undefined});
    }
  });
  // A fresh callback each render makes React re-attach the ref after every commit, so the latest
  // value is republished; `publish` itself drops equal values, so subscribers see no churn. Same
  // idiom as useAssistantTemplateEditorContext's synchronizeRef.
  const synchronizeRef = (node: HTMLSpanElement | null) => {
    if (isNotNil(node)) {
      publish(entities);
    }
  };

  return {ownerRef, synchronizeRef};
}
